import {
  ADMIN_LOGOUT_URL,
  ADMIN_ME_URL,
  BLOGS_URL,
  CURRENT_FOCUS_URL,
  EMPLOYEES_URL,
  LOCALIZATIONS_URL,
  MESSAGES_URL,
  PORTFOLIOS_URL,
  SKILLS_URL,
} from "../constants";

export const getAdminToken = () => {
  if (typeof window === "undefined") return "";
  return (
    sessionStorage.getItem("admin_session") ||
    localStorage.getItem("admin_token") ||
    ""
  );
};

export const redirectToAdminLogin = async () => {
  if (typeof window !== "undefined") {
    sessionStorage.removeItem("admin_session");
    localStorage.removeItem("admin_token");
    try {
      await fetch(ADMIN_LOGOUT_URL, {
        method: "POST",
        credentials: "include",
      });
    } catch {
      // ignore network errors during redirect
    }
    if (window.location.pathname !== "/admin/login") {
      window.location.replace("/admin/login");
    }
  }
};

export const requireAdminToken = () => {
  const token = getAdminToken();
  if (!token) {
    redirectToAdminLogin();
    return "";
  }
  return token;
};

const withAuthHeaders = (token, headers = {}) => {
  const nextHeaders = { ...headers };
  if (token && token !== "active" && token !== "session-active") {
    nextHeaders.Authorization = `Bearer ${token}`;
  }
  return nextHeaders;
};

export const authJsonFetch = async (
  url,
  { method = "GET", token, body } = {},
) => {
  const response = await fetch(url, {
    method,
    headers: withAuthHeaders(token, { "Content-Type": "application/json" }),
    body: body ? JSON.stringify(body) : undefined,
    credentials: "include",
  });

  const payload = await response.json().catch(() => ({}));
  if (response.status === 401) {
    redirectToAdminLogin();
    throw new Error(payload.message || "Unauthorized");
  }
  if (!response.ok) {
    throw new Error(
      payload.message || `Request failed with status ${response.status}`,
    );
  }

  return payload;
};

export const authFormFetch = async (
  url,
  { method = "POST", token, formData } = {},
) => {
  const response = await fetch(url, {
    method,
    headers: withAuthHeaders(token),
    body: formData,
    credentials: "include",
  });

  const payload = await response.json().catch(() => ({}));
  if (response.status === 401) {
    redirectToAdminLogin();
    throw new Error(payload.message || "Unauthorized");
  }
  if (!response.ok) {
    throw new Error(
      payload.message || `Request failed with status ${response.status}`,
    );
  }

  return payload;
};

export const adminApi = {
  auth: {
    me: () => authJsonFetch(ADMIN_ME_URL),
    logout: async () => {
      if (typeof window !== "undefined") {
        sessionStorage.removeItem("admin_session");
        localStorage.removeItem("admin_token");
      }
      try {
        await fetch(ADMIN_LOGOUT_URL, {
          method: "POST",
          credentials: "include",
        });
      } catch {
        // ignore network error
      }
    },
  },
  skills: {
    list: (token) => authJsonFetch(SKILLS_URL, { token }),
    create: (token, formData) => authFormFetch(SKILLS_URL, { token, formData }),
    update: (token, id, formData) =>
      authFormFetch(`${SKILLS_URL}/${id}`, { method: "PUT", token, formData }),
    remove: (token, id) =>
      authJsonFetch(`${SKILLS_URL}/${id}`, { method: "DELETE", token }),
  },
  portfolios: {
    list: (token) => authJsonFetch(PORTFOLIOS_URL, { token }),
    create: (token, formData) =>
      authFormFetch(PORTFOLIOS_URL, { token, formData }),
    update: (token, id, formData) =>
      authFormFetch(`${PORTFOLIOS_URL}/${id}`, {
        method: "PUT",
        token,
        formData,
      }),
    remove: (token, id) =>
      authJsonFetch(`${PORTFOLIOS_URL}/${id}`, { method: "DELETE", token }),
  },
  messages: {
    list: (token) => authJsonFetch(MESSAGES_URL, { token }),
    remove: (token, id) =>
      authJsonFetch(`${MESSAGES_URL}/${id}`, { method: "DELETE", token }),
  },
  localizations: {
    listFlat: (token) => authJsonFetch(`${LOCALIZATIONS_URL}/flat`, { token }),
    upsert: (token, body) =>
      authJsonFetch(`${LOCALIZATIONS_URL}/upsert`, {
        method: "POST",
        token,
        body,
      }),
  },
  employees: {
    list: (token) => authJsonFetch(EMPLOYEES_URL, { token }),
    regenerate: (token, body) =>
      authJsonFetch(`${EMPLOYEES_URL}/regenerate`, {
        method: "POST",
        token,
        body,
      }),
  },
  blogs: {
    list: (token) => authJsonFetch(BLOGS_URL, { token }),
    create: (token, body) =>
      authJsonFetch(BLOGS_URL, { method: "POST", token, body }),
    update: (token, id, body) =>
      authJsonFetch(`${BLOGS_URL}/${id}`, { method: "PUT", token, body }),
    remove: (token, id) =>
      authJsonFetch(`${BLOGS_URL}/${id}`, { method: "DELETE", token }),
  },
  currentFocus: {
    list: (token) => authJsonFetch(`${CURRENT_FOCUS_URL}/manage`, { token }),
    createCategory: (token, body) =>
      authJsonFetch(`${CURRENT_FOCUS_URL}/categories`, {
        method: "POST",
        token,
        body,
      }),
    updateCategory: (token, id, body) =>
      authJsonFetch(`${CURRENT_FOCUS_URL}/categories/${id}`, {
        method: "PUT",
        token,
        body,
      }),
    patchCategory: (token, id, body) =>
      authJsonFetch(`${CURRENT_FOCUS_URL}/categories/${id}`, {
        method: "PATCH",
        token,
        body,
      }),
    removeCategory: (token, id) =>
      authJsonFetch(`${CURRENT_FOCUS_URL}/categories/${id}`, {
        method: "DELETE",
        token,
      }),
    create: (token, body) =>
      authJsonFetch(CURRENT_FOCUS_URL, { method: "POST", token, body }),
    update: (token, id, body) =>
      authJsonFetch(`${CURRENT_FOCUS_URL}/${id}`, {
        method: "PUT",
        token,
        body,
      }),
    remove: (token, id) =>
      authJsonFetch(`${CURRENT_FOCUS_URL}/${id}`, { method: "DELETE", token }),
  },
};
